import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import { LoyaltyProgram } from "@/interfaces/loyaltyPrograms";
import { PaginatedResponse } from "@/interfaces/paginatedResponse";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ref, watch } from "vue";

interface Props {
  immediate?: boolean;
  params: Params;
  config?: Config;
}

interface Config {
  errorHandling?: boolean;
}

interface Params {
  id: string | Number;
  page?: number;
}

interface CreateParams {
  id: string | Number;
  name: string;
  serviceId: string | Number;
}

const getChainLoyalProgram = async (
  params: Params,
  config?: Config,
): Promise<PaginatedResponse<LoyaltyProgram>> => {
  const { errorHandling = true } = config || {};

  const { data } = await axios.get<PaginatedResponse<LoyaltyProgram>>(
    `/api/internal/chains/${params.id}/loyalty-programs?page=${params.page}`,
    { errorHandling } as CustomAxiosRequestConfig,
  );

  return data;
};

const createChainLoyalProgram = async (params: CreateParams) => {
  const { data } = await axios.post(
    `/api/internal/chains/${params.id}/loyalty-programs`,
    {
      name: params.name,
      serviceId: params.serviceId,
    },
  );

  return data;
};

const useChainLoyaltyProgram = ({
  immediate = true,
  params,
  config,
}: Props) => {
  const page = ref(1);

  const setPage = (newPage: number) => {
    page.value = newPage;
  };

  const { data, isLoading, refetch } = useQuery(
    ["chain-loyalty-program", params.id],
    () =>
      getChainLoyalProgram(
        {
          id: params.id,
          page: page.value,
        },
        {
          errorHandling: config?.errorHandling,
        },
      ),
    {
      enabled: immediate,
    },
  );

  watch(page, () => {
    refetch();
  });

  const createMutation = useMutation((params: CreateParams) =>
    createChainLoyalProgram(params),
  );

  return {
    data,
    isLoading,
    refetch,
    page,
    setPage,

    createChainLoyalProgramMutation: createMutation.mutate,
    createChainLoyalProgramMutationLoading: createMutation.isLoading,
    createChainLoyalProgramMutationAsync: createMutation.mutateAsync,
  };
};

export default useChainLoyaltyProgram;
